import { Inject, LOCALE_ID, Pipe, PipeTransform } from '@angular/core';
import { formatDate } from '@angular/common';
import { UserTimezoneService } from 'src/app/services/user-timezone.service';

/**
 * `{{ value | userDate:'d MMM yy, h:mm a' }}` — Angular's `date` pipe, but in the user's PROFILE
 * timezone (Profile → Account Details → Timezone), not the browser's.
 *
 * Why it exists (2026-09-15): the server stores and now sends every timestamp in UTC. Angular's own
 * `date` pipe only understands offsets like "+0530" — it silently ignores IANA names such as
 * "Asia/Kolkata" and falls back to the browser zone, so `| date:'…':userTimezone` looked right only
 * when the browser happened to be in the same zone. This pipe turns the IANA zone into the correct
 * offset FOR THAT MOMENT (so daylight saving is handled) and then formats exactly like `date`.
 *
 * Optional 2nd argument overrides the zone (e.g. a notification's recipientTimezone, a sales rep's
 * own timezone). Same format strings as Angular's DatePipe. Empty/invalid values → ''.
 */
@Pipe({ name: 'userDate', standalone: true, pure: true })
export class UserDatePipe implements PipeTransform {
  constructor(
    private readonly userTimezoneService: UserTimezoneService,
    @Inject(LOCALE_ID) private readonly locale: string,
  ) {}

  transform(value: string | number | Date | null | undefined, format = 'mediumDate', timeZone?: string | null): string {
    if (value === null || value === undefined || value === '') return '';
    // A plain calendar date ("2026-09-15": consignment date, purchase date, processing date…) is a
    // day, not a moment — it must show that same day for everyone, so it is never shifted.
    if (typeof value === 'string' && CALENDAR_DATE.test(value)) {
      const [y, m, d] = value.split('-').map(Number);
      return formatDate(new Date(y, m - 1, d), format, this.locale);
    }
    const date = value instanceof Date ? value : new Date(value);
    if (isNaN(date.getTime())) return '';
    // A Date object shown without a time is a date picked on this device (local midnight) — keep it.
    if (value instanceof Date && !hasTimeTokens(format)) {
      return formatDate(date, format, this.locale);
    }
    const zone = timeZone || (this.userTimezoneService.hasProfileTimezone ? this.userTimezoneService.timezone : null);
    if (!zone) return formatDate(date, format, this.locale); // public page: viewer's own timezone
    try {
      return formatDate(date, format, this.locale, offsetFor(zone, date));
    } catch {
      return formatDate(date, format, this.locale);
    }
  }
}

const CALENDAR_DATE = /^\d{4}-\d{2}-\d{2}$/;
const NAMED_TIME_FORMATS = new Set(['short', 'medium', 'long', 'full', 'shortTime', 'mediumTime', 'longTime', 'fullTime']);
const NAMED_DATE_FORMATS = new Set(['shortDate', 'mediumDate', 'longDate', 'fullDate']);

function hasTimeTokens(format: string): boolean {
  if (NAMED_TIME_FORMATS.has(format)) return true;
  if (NAMED_DATE_FORMATS.has(format)) return false; // "mediumDate" contains an "m" — not a time
  return /[hHms]/.test(format.replace(/'[^']*'/g, '').replace(/M+/g, ''));
}

/** Profile timezone for code outside templates (stored by UserTimezoneService); null when not logged in. */
export function profileTimeZone(): string | null {
  try { return localStorage.getItem('tms-user-timezone'); } catch { return null; }
}

/** `formatDate` in the profile timezone (browser zone when not logged in). */
export function formatInProfileZone(value: string | number | Date, format: string, locale = 'en-US'): string {
  const date = value instanceof Date ? value : new Date(value);
  if (isNaN(date.getTime())) return '';
  const zone = profileTimeZone();
  return formatDate(date, format, locale, zone ? offsetFor(zone, date) : undefined);
}

/** "+0530" style offset of an IANA zone at the given moment. Falls back to the browser zone. */
export function offsetFor(timeZone: string, at: Date): string | undefined {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone, hourCycle: 'h23',
      year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit',
    }).formatToParts(at);
    const get = (t: string) => Number(parts.find(p => p.type === t)?.value);
    const asUtc = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour') % 24, get('minute'), get('second'));
    const minutes = Math.round((asUtc - Math.floor(at.getTime() / 1000) * 1000) / 60000);
    const sign = minutes >= 0 ? '+' : '-';
    const abs = Math.abs(minutes);
    return `${sign}${String(Math.floor(abs / 60)).padStart(2, '0')}${String(abs % 60).padStart(2, '0')}`;
  } catch {
    return undefined;
  }
}
